import { cn } from "@u/cn";
import Button from "@ui/button";
import { chipVariants } from "@ui/chip-variants";
import { Input } from "@ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/select";
import { useTranslation } from "react-i18next";
import type { DashboardFilterConfig } from "./dashboardData";

// Sentinel for the "all" / clear row — Radix Select forbids an empty-string
// item value, so we map this back to an empty selection.
const ALL_VALUE = "__ds_all__";

export interface DashboardFiltersProps {
  filters: DashboardFilterConfig[];
  selected: Record<string, string[]>;
  onChange(field: string, values: string[]): void;
  onReset?(): void;
  className?: string;
}

export function DashboardFilters({
  filters,
  selected,
  onChange,
  onReset,
  className,
}: DashboardFiltersProps) {
  const { t } = useTranslation();
  if (filters.length === 0) return null;

  const hasAny = Object.values(selected).some((v) => v.length > 0);

  const toggle = (field: string, value: string) => {
    const current = selected[field] ?? [];
    onChange(
      field,
      current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value],
    );
  };

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {filters.map((filter) => {
        const current = selected[filter.field] ?? [];

        if (filter.kind === "text") {
          return (
            <Input
              bare
              size="sm"
              key={filter.field}
              type="text"
              value={current[0] ?? ""}
              placeholder={filter.label}
              onChange={(e) =>
                onChange(filter.field, e.target.value ? [e.target.value] : [])
              }
              className="h-8 rounded-xl border border-border/40 bg-transparent px-3 md:text-xs"
            />
          );
        }

        if (filter.kind === "select" || filter.kind === "daterange") {
          return (
            <Select
              key={filter.field}
              value={current[0] ?? undefined}
              onValueChange={(v) =>
                onChange(filter.field, v === ALL_VALUE ? [] : [v])
              }
            >
              <SelectTrigger
                aria-label={filter.label}
                size="sm"
                 className="h-8 w-auto min-w-32 rounded-xl border border-border/40 bg-transparent px-3 md:text-xs"
              >
                <SelectValue placeholder={filter.label} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE} className="text-xs text-muted-foreground">
                  {filter.label}
                </SelectItem>
                {filter.options?.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }

        // multiselect | status | tags => toggle chips
        return (
          <div
            key={filter.field}
            role="group"
            aria-label={filter.label}
            className="flex flex-wrap items-center gap-1"
          >
            {filter.options?.map((opt) => {
              const active = current.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggle(filter.field, opt.value)}
                  className={chipVariants({ size: "sm", active })}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        );
      })}

      {hasAny && onReset && (
        <Button
          design="ghost"
          size="sm"
          label={t("ds_clear_filters", { defaultValue: "Clear" })}
          onClick={onReset}
          className="text-xs h-8"
        />
      )}
    </div>
  );
}
